import { GetGeneralInformationResponse } from "@1games-ru/region-shared/dist/backend/requests/faction";
import {
  type SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/helpers/cn";
import { Search2Icon, UsersIcon } from "@/shared/icons";
import { Button, TextInput } from "@/shared/ui";
import { TableVirtualize } from "@/shared/widgets/TableVirtualize";
import { ColleaguesTabsEnum } from "@/domains/faction/constants/colleaguesTabsEnum";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import { ParticipantRow } from "@/domains/faction/constants/participantRow";
import { factionsList } from "@/domains/faction/helpers/factionsList";
import useColleaguesColumns from "@/domains/faction/hooks/useColleaguesColumns";
import useGetBlackList from "@/domains/faction/hooks/useGetBlackList";
import useGetInfo from "@/domains/faction/hooks/useGetInfo";
import useGetParticipants from "@/domains/faction/hooks/useGetParticipants";
import useGetRequests from "@/domains/faction/hooks/useGetRequests";
import FactionColeaguesModalsContainer from "@/domains/faction/containers/FactionColeaguesModalsContainer";
import FactionPlayerInfo from "@/domains/faction/components/FactionPlayerInfo";
import FactionTabsRow from "@/domains/faction/components/FactionTabsRow";

type Props = {
  faction: FactionEnum;
};

export default function FactionColleaguesContainer({ faction }: Props) {
  const { t } = useTranslation("faction");

  const [inputValue, setInputValue] = useState("");
  const [isPlayerInfo, setIsPlayerInfo] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [isUpModal, setIsUpModal] = useState(false);
  const [isDownModal, setIsDownModal] = useState(false);
  const [isBonusesModal, setIsBonusesModal] = useState(false);
  const [isPostModal, setIsPostModal] = useState(false);
  const [isDepartmentModal, setIsDepartmentModal] = useState(false);
  const [isBlackListModal, setIsBlackListModal] = useState(false);
  const [isRemoveFromBlackListModal, setIsRemoveFromBlackListModal] = useState(false);
  const [isDismissModal, setIsDismissModal] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: factionInfo } = useGetInfo();

  const tabs = useMemo(
    () => [
      ColleaguesTabsEnum.PARTICIPANTS,
      ColleaguesTabsEnum.BLACKLIST,
      ColleaguesTabsEnum.REQUESTS,
      ColleaguesTabsEnum.DEPARTMENT,
    ],
    [],
  );
  const [activeTab, setActiveTab] = useState<ColleaguesTabsEnum>(tabs[0]);

  const { participantsData, isParticipantsLoading } = useGetParticipants(activeTab);
  const { blackListData, isBlackListLoading } = useGetBlackList(activeTab);
  const { requestsData, isRequestsLoading } = useGetRequests(activeTab);

  const participantsList = useMemo<ParticipantRow[]>(() => {
    if (!participantsData || !factionInfo) return [];

    const info = factionInfo as GetGeneralInformationResponse;

    return participantsData.map<ParticipantRow>((item) => {
      const rank = info.ranks.find((el) => el.id === item.rankId);
      const position = info.rankPositions.find((el) => el.id === item.rankPositionId);
      const department = info.departments.find((el) => el.id === item.departmentId);

      return {
        ...item,
        ...(rank && { rankName: rank.name, rankLevel: rank.level }),
        ...(position && { positionName: position.name }),
        ...(department && { tagName: department.tag, departmentName: department.name }),
      };
    });
  }, [factionInfo, participantsData]);

  const blackList = useMemo(() => blackListData || [], [blackListData]);
  const requestsList = useMemo(() => requestsData || [], [requestsData]);

  const selectedRowInfo = useMemo<ParticipantRow | null>(() => {
    if (selectedRowId === null) return null;

    let arr = [];
    if (
      activeTab === ColleaguesTabsEnum.PARTICIPANTS ||
      activeTab === ColleaguesTabsEnum.DEPARTMENT
    ) {
      arr = participantsList;
    }
    if (activeTab === ColleaguesTabsEnum.BLACKLIST) arr = blackList;

    return arr.find((p) => p.characterId === selectedRowId) ?? null;
  }, [selectedRowId, activeTab, blackList, participantsList]);

  const isLoading = useMemo(() => {
    switch (activeTab) {
      case ColleaguesTabsEnum.PARTICIPANTS:
        return isParticipantsLoading || isParticipantsLoading === undefined;
      case ColleaguesTabsEnum.BLACKLIST:
        return isBlackListLoading || isBlackListLoading === undefined;
      case ColleaguesTabsEnum.REQUESTS:
        return isRequestsLoading || isRequestsLoading === undefined;
      case ColleaguesTabsEnum.DEPARTMENT:
        return isParticipantsLoading || isParticipantsLoading === undefined;
      default:
        return false;
    }
  }, [activeTab, isBlackListLoading, isParticipantsLoading, isRequestsLoading]);

  const { getParticipantsColumns, getBlackListColumns, getRequestsColumns, getDepartamentColumns } =
    useColleaguesColumns();

  const baseData = useMemo(() => {
    switch (activeTab) {
      case ColleaguesTabsEnum.PARTICIPANTS:
        return participantsList;
      case ColleaguesTabsEnum.BLACKLIST:
        return blackList;
      case ColleaguesTabsEnum.REQUESTS:
        return requestsList;
      case ColleaguesTabsEnum.DEPARTMENT:
        return participantsList;
      default:
        return [];
    }
  }, [activeTab, blackList, participantsList, requestsList]);

  const columns = useMemo(() => {
    switch (activeTab) {
      case ColleaguesTabsEnum.PARTICIPANTS:
        return getParticipantsColumns();
      case ColleaguesTabsEnum.BLACKLIST:
        return getBlackListColumns();
      case ColleaguesTabsEnum.REQUESTS:
        return getRequestsColumns();
      case ColleaguesTabsEnum.DEPARTMENT:
        return getDepartamentColumns();
      default:
        return [];
    }
  }, [
    activeTab,
    getParticipantsColumns,
    getBlackListColumns,
    getRequestsColumns,
    getDepartamentColumns,
  ]);

  useEffect(() => {
    setSorting([]);
  }, [activeTab]);

  const filteredData = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) return baseData;
    return baseData.filter(
      (row) =>
        row.firstName.toLowerCase().includes(query) || row.lastName.toLowerCase().includes(query),
    );
  }, [baseData, inputValue]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const actionsList = useMemo(
    () => [
      () => setIsUpModal(true),
      () => setIsDownModal(true),
      () => setIsBonusesModal(true),
      () => setIsPostModal(true),
      () => setIsDepartmentModal(true),
      () => setIsBlackListModal(true),
    ],
    [],
  );

  const onDismiss = useCallback(() => setIsDismissModal(true), []);
  const onUpRank = useCallback(() => setIsUpModal(true), []);

  const onTableRowClick = useCallback(
    (row: ParticipantRow) => {
      if (activeTab === ColleaguesTabsEnum.PARTICIPANTS && participantsList?.length > 0) {
        setSelectedRowId(row.characterId);
        setIsPlayerInfo(true);
      }
    },
    [activeTab, participantsList?.length],
  );

  const onBack = useCallback(() => {
    setIsPlayerInfo(false);
    setSelectedRowId(null);
  }, []);

  const selectList = useMemo(() => {
    switch (activeTab) {
      case ColleaguesTabsEnum.PARTICIPANTS:
        return [
          { text: t("faction.dossier"), onClick: () => setIsPlayerInfo(true) },
          { text: t("faction.dismiss"), onClick: () => setIsDismissModal(true) },
          { text: t("faction.upRank"), onClick: () => setIsUpModal(true) },
          { text: t("faction.downRank"), onClick: () => setIsDownModal(true) },
          { text: t("faction.giveBonus"), onClick: () => setIsBonusesModal(true) },
          { text: t("faction.addInBlackList"), onClick: () => setIsBlackListModal(true) },
          { text: t("faction.changeDepartment"), onClick: () => setIsDepartmentModal(true) },
        ];
      case ColleaguesTabsEnum.BLACKLIST:
        return [
          {
            text: t("faction.removeFromBlackList"),
            onClick: () => setIsRemoveFromBlackListModal(true),
          },
        ];
      case ColleaguesTabsEnum.DEPARTMENT:
        return [{ text: t("faction.changeDepartment"), onClick: () => setIsDepartmentModal(true) }];
      default:
        return null;
    }
  }, [activeTab, t]);

  const modals = (
    <FactionColeaguesModalsContainer
      selectedRowInfo={selectedRowInfo as ParticipantRow}
      faction={faction}
      isUpModal={isUpModal}
      setIsUpModal={setIsUpModal}
      isDownModal={isDownModal}
      setIsDownModal={setIsDownModal}
      isBonusesModal={isBonusesModal}
      setIsBonusesModal={setIsBonusesModal}
      isPostModal={isPostModal}
      setIsPostModal={setIsPostModal}
      isDepartmentModal={isDepartmentModal}
      setIsDepartmentModal={setIsDepartmentModal}
      isBlackListModal={isBlackListModal}
      setIsBlackListModal={setIsBlackListModal}
      isRemoveFromBlackListModal={isRemoveFromBlackListModal}
      setIsRemoveFromBlackListModal={setIsRemoveFromBlackListModal}
      isDismissModal={isDismissModal}
      setIsDismissModal={setIsDismissModal}
      onBack={onBack}
    />
  );

  return isPlayerInfo ? (
    <FactionPlayerInfo
      faction={faction}
      rowInfo={selectedRowInfo as ParticipantRow}
      onBack={onBack}
      modals={modals}
      actionsList={actionsList}
      onDismiss={onDismiss}
      onUpRank={onUpRank}
    />
  ) : (
    <div className="h-full p-6 flex flex-col min-h-0">
      <FactionTabsRow
        title={t("faction.titles.colleagues")}
        Icon={UsersIcon}
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab as unknown as (v: string) => void}
        faction={faction}
        i18Path="faction.colleaguesTabs"
      />
      <TextInput
        value={inputValue}
        onChange={setInputValue}
        maxLength={40}
        type="text"
        inputClassName="h-9.5 text-14"
        placeholder={t("faction.searchForUsers")}
        icon={<Search2Icon className="size-4.5" />}
      />
      <div className="relative flex flex-col grow min_h-0">
        <div className="relative grow min-h-0">
          <div className="absolute z-2 bottom-0 left-0 w-full h-10 bg-gradient-to-t from-dark-900 to-transparent pointer-events-none" />
          <div className="absolute z-2 bottom-0 left-0 w-full h-8 bg-gradient-to-t from-dark-900 to-transparent pointer-events-none" />
          <div className="absolute inset-0 overflow-x-clip w-[calc(100%+1.0625rem)]">
            <div className="h-full">
              <TableVirtualize
                table={table}
                isLoading={isLoading}
                onRowClick={onTableRowClick}
                scrollbarPaddingBottom="pb-3.5"
                scrollbarPaddingRight="pr-[0.8125rem]"
                setSelectedRowInfo={(row) => setSelectedRowId((row as ParticipantRow)?.characterId)}
                renderContextBlock={
                  selectList
                    ? ({ row, close }) => {
                        const openForRow = (fn: () => void) => () => {
                          setSelectedRowId((row as ParticipantRow)?.characterId);
                          fn();
                          close();
                        };
                        return (
                          <div className="flex flex-col w-[11.125rem] py-2 bg-dark-800 border border-[#242424] rounded-2xl">
                            {selectList.map((item) => (
                              <Button
                                key={item.text}
                                variant="solid"
                                className="overflow-clip group relative flex justify-start h-auto min-h-10 mx-2 py-3 px-4.5 text-13 text-left font-medium capitalize whitespace-normal break-words bg-transparent"
                                onClick={openForRow(item.onClick)}
                              >
                                <div
                                  className={cn(
                                    "absolute top-0 left-0 size-full opacity-0 group-hover:opacity-100",
                                    factionsList[faction]?.gradient,
                                  )}
                                />
                                <p className="relative z-1">{item.text}</p>
                              </Button>
                            ))}
                          </div>
                        );
                      }
                    : null
                }
              />
            </div>
          </div>
        </div>
      </div>
      {modals}
    </div>
  );
}
